'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useSession } from 'next-auth/react';
// import Cookies from 'js-cookie';
import SiteHeader from '@/components/organisms/SiteHeader';
import SiteFooter from '@/components/organisms/SiteFooter';
import { fontsizeMap } from '@/util/const';
import { fetchPromise } from '@/util/fetch';
import { Op } from 'sequelize'; // Assuming Op might be needed for client-side fetch logic if replicating query

export default function MainContentWrapper({
  children,
  getCouponCount,
}: {
  children: React.ReactNode;
  getCouponCount: () => Promise<number>;
}) {
  const { data: session } = useSession();
  const [fontSize, setFontSize] = useState<'s' | 'm' | 'l'>('s');
  const [isMobile, setIsMobile] = useState(false);
  const [couponCount, setCouponCount] = useState(0);

  // Read cookies on mount
  useEffect(() => {
    const fsCookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith('fontSize='))
      ?.split('=')[1] as 's' | 'm' | 'l' | undefined;

    const mobileCookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith('mobile='))
      ?.split('=')[1];

    if (fsCookie && ['s', 'm', 'l'].includes(fsCookie)) {
      setFontSize(fsCookie);
    }
    setIsMobile(mobileCookie === 'true');
  }, []);

  // Apply font size to HTML tag
  useEffect(() => {
    document.documentElement.style.fontSize = fontsizeMap[fontSize];
  }, [fontSize]);

  // Fetch coupon count when session changes
  useEffect(() => {
    const fetchCouponCount = async () => {
      if (session?.user?.contact?.uuid__c) {
        try {
          const count = await getCouponCount(); // Call the passed function to get coupon count
          setCouponCount(count);
        } catch (error) {
          console.error('Error fetching coupon count:', error);
          setCouponCount(0);
        }
      } else {
        setCouponCount(0); // Reset if no user or contact UUID
      }
    };

    fetchCouponCount();
  }, [session]); // Re-fetch when session changes

  const handleSetFontsizeCookies = useCallback((size: 's' | 'm' | 'l') => {
    document.cookie = `fontSize=${size}; max-age=${60 * 60 * 24 * 365}; path=/`;
    setFontSize(size);
  }, []);

  return (
    <>
      {!isMobile && (
        <SiteHeader
          // Pass necessary props derived from client-side state/hooks
          setFontsizeCookies={handleSetFontsizeCookies}
          fontSize={fontSize}
          couponCount={couponCount}
          // session is now handled internally by useSession in SiteHeader
        />
      )}
      {children}
      {/* SiteFooter now handles session internally with useSession */}
      <SiteFooter />
    </>
  );
}
